import { useUpdateChildren } from 'common/queries-and-mutations/children';
import { useGetGuardianList } from 'common/queries-and-mutations/guardian';
import Button from 'components/Form/Button';
import GenderField from 'components/Form/GenderField';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import { Alert } from 'components/Toast';
import { differenceInYears, parse } from 'date-fns';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import AddGuardianModal from '../../Guardians/AddGuardianModal';
import withCreatePortal from 'components/HOC/withCreatePortal';
import AddSubstituteGuardianModal from 'apps/user/OnBoarding/AddSubstituteGuardianModal';
import { Child } from 'service/dto/children.dto';
import CustomSelect from 'components/Form/CustomSelect';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  child: Child;
};

const EnhancedAddGuardianForm = withCreatePortal(AddGuardianModal);
const EnhancedAddSubstituteGuardianForm = withCreatePortal(
  AddSubstituteGuardianModal
);

export default function EditChildrenModal({
  onClose,
  onSuccess,
  child
}: Readonly<Props>) {
  const [showAddGuardian, setShowAddGuardian] = useState(false);
  const [showAddSubsituteGuardian, setShowAddSubsituteGuardian] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useUpdateChildren();
  const { data: guardians, refetch } = useGetGuardianList();

  const formatGuardians = useMemo(() => {
    return (guardians || []).map(guardian => ({
      value: guardian.id!,
      label: `${guardian.guardianFirstName} ${guardian.guardianMiddleName} ${guardian.guardianLastName} (${guardian.guardianType})`
    }));
  }, [guardians]);

  const formik = useFormik({
    initialValues: {
      guardian:
        child.guardian ?? child.substituteGuardian
          ? {
              value: child.guardianId! ?? child.substituteGuardianId!,
              label: `${
                (child.guardian ?? child.substituteGuardian).guardianFirstName
              } ${
                (child.guardian ?? child.substituteGuardian)
                  .guardianMiddleName || ''
              } ${
                (child.guardian ?? child.substituteGuardian).guardianLastName
              } (${(child.guardian ?? child.substituteGuardian).guardianType})`
            }
          : null,
      childFirstName: child.childFirstName,
      childMiddleName: child.childMiddleName,
      childLastName: child.childLastName,
      gender: child.gender,
      phoneNumber: child.phoneNumber,
      dateOfBirth: child.dateOfBirth,
      isBeneficiary: !!child.beneficiaryId,
      hasGuardian: !!child.guardianId
    },
    validationSchema: Yup.object().shape({
      childFirstName: Yup.string()
        .required('Children First Name is required')
        .nullable(),
      // childMiddleName: Yup.string()
      //   .required('Children Middle Name is required')
      //   .nullable(),
      childLastName: Yup.string()
        .required('Children Last Name is required')
        .nullable(),
      gender: Yup.string().required('Gender is required').nullable(),
      phoneNumber: Yup.string().required('Phone number is required').nullable(),
      dateOfBirth: Yup.date().required('Date of birth is required').nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onChildSubmit(values);
    }
  });

  const shouldAssignGuardian = useMemo(() => {
    const currentDate = new Date();
    const birthdate = parse(
      formik.values.dateOfBirth,
      'yyyy-MM-dd',
      new Date()
    );
    const age = differenceInYears(currentDate, birthdate);
    return age < 18;
  }, [formik.values.dateOfBirth]);

  const onChildSubmit = (formValue: any) => {
    if (!formValue.hasGuardian && shouldAssignGuardian && !formValue.guardian) {
      setIsSubmitting(false);
      toast(<Alert message="Please select a guardian" type="error" />);
      return;
    }

    let payload;
    if (formValue.guardian?.value) {
      payload = {
        ...formValue,
        guardianId: Number(formValue.guardian.value)
      };
    } else {
      payload = formValue;
    }

    mutate({
      childrenId: child.id,
      children: payload
    });
  };

  //Execute if guardian Successfully create
  const onGuardianComplete = () => {
    refetch();
    setShowAddGuardian(false);
    setShowAddSubsituteGuardian(true); //Show add substitute
  };

  const onSubsituteGuardianComplete = () => {
    setShowAddGuardian(false);
    setShowAddSubsituteGuardian(false);
  };

  // Show modal to create guardian if child is under age
  useEffect(() => {
    //The children is not under age, then show Successful message
    if (isSuccess && !isError) {
      onSuccess();
      toast(<Alert message="Child Successfully Updated" type="success" />);
      setIsSubmitting(false);
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccess, child.guardianId]);

  //Execute this if the children in under age
  // const showAddGuardianModal = () => {
  //   if (guardians?.length) {
  //     setShowAskToConfirm(true);
  //   } else {
  //     setShowAddGuardian(true);
  //   }
  //   setShowAssigneGuardian(true);
  //   setShowAddGuardian(false);
  // };

  const renderModal = useCallback(() => {
    if (showAddGuardian) {
      return (
        <EnhancedAddGuardianForm
          onClose={() => setShowAddGuardian(false)}
          onSuccess={onGuardianComplete}
        />
      );
    } else if (showAddSubsituteGuardian) {
      return (
        <EnhancedAddSubstituteGuardianForm
          onClose={() => setShowAddSubsituteGuardian(false)}
          onComplete={() => {
            refetch();
            onSubsituteGuardianComplete();
          }}
        />
      );
    } else {
      return (
        <InfoModal
          width="w-[55%]"
          className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
        >
          <div>
            <div className="flex flex-row items-center justify-between pl-6">
              <p className="text-2xl font-bold text-[#353535]">Edit Child</p>
              <button type="button" onClick={onClose}>
                <Icon name={Icons.Close} />
              </button>
            </div>
            <form
              autoComplete="false"
              className="mt-10 px-6"
              onSubmit={formik.handleSubmit}
            >
              <div className="my-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`childFirstName`}
                      placeholder=""
                      value={formik.values.childFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Child First Name"
                      error={
                        formik.touched.childFirstName
                          ? formik.errors.childFirstName
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`childMiddleName`}
                      placeholder=""
                      value={formik.values.childMiddleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Child Middle Name"
                      error={
                        formik.touched.childMiddleName
                          ? formik.errors.childMiddleName
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`childLastName`}
                      placeholder=""
                      value={formik.values.childLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Child Last Name"
                      error={
                        formik.touched.childLastName
                          ? formik.errors.childLastName
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`phoneNumber`}
                      placeholder=""
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Phone Number"
                      error={
                        formik.touched.phoneNumber
                          ? formik.errors.phoneNumber
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`dateOfBirth`}
                      placeholder=""
                      value={formik.values.dateOfBirth}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Date of Birth"
                      error={
                        formik.touched.dateOfBirth
                          ? formik.errors.dateOfBirth
                          : ''
                      }
                      type="date"
                      size="lg"
                      validateDate={false}
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  {shouldAssignGuardian && (
                    <CustomSelect
                      options={[
                        ...formatGuardians,
                        {
                          value: 'new',
                          label: '+ Create new guardian'
                        }
                      ]}
                      selectedOption={formik.values.guardian}
                      handleOptionChange={option => {
                        if (option?.value === 'new') {
                          setShowAddGuardian(true);
                          return;
                        }
                        formik.setFieldValue(`guardian`, option);
                      }}
                      label="Select guardian"
                      name="guardian"
                      isSearchable
                      className="h-[3.2rem] rounded-xl"
                      error={
                        formik.touched.guardian ? formik.errors.guardian : ''
                      }
                    />
                  )}
                  <div className="-mt-5">
                    <GenderField
                      gender={formik.values.gender}
                      onSelect={gender =>
                        formik.setFieldValue(`gender`, gender)
                      }
                      error={formik.touched.gender ? formik.errors.gender : ''}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Icon name={Icons.Required} />
                  <span className="text-[10px] text-[#7A7C87]">
                    Indicates Required
                  </span>
                </div>
              </div>

              <div className="flex w-full items-end justify-end gap-4">
                <Button
                  variant="primary"
                  size="custom"
                  className="mt-5 w-[9rem] rounded p-2.5"
                  type="submit"
                  label="Update Child"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </form>
          </div>
        </InfoModal>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik, showAddGuardian, guardians, showAddSubsituteGuardian]);

  return renderModal();
}
